import { useState, useCallback } from 'react'
import { useToast } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useApi } from '../contexts/ApiContext'
import {
	CordeCommonModelsDtoContactContact as ContactDto,
	CordeCommonModelsDtoNotificationsCreateContactRequest,
	CordeCommonModelsDtoNotificationsUpdateContactRequest,
	CordeCommonModelsDtoNotificationsSetPrimaryContactRequest,
	CordeCommonModelsDtoNotificationsVerifyContactRequest,
	CordeCommonModelsDtoEntityEntity as EntityDto,
} from '../api/generated/v1/api'

export interface ContactFormData {
	type: string
	value: string
	label?: string
	description?: string
	isPrimary?: boolean
}

export const useContacts = () => {
	const { NotificationsApi } = useApi()
	const toast = useToast()
	const { t } = useTranslation()
	const [isLoading, setIsLoading] = useState(false)

	// Create contact
	const createContact = useCallback(async (
		entity: EntityDto,
		contactData: ContactFormData
	): Promise<ContactDto | null> => {
		setIsLoading(true)
		try {
			const request: CordeCommonModelsDtoNotificationsCreateContactRequest = {
				contact: {
					entity,
					type: contactData.type,
					value: contactData.value,
					label: contactData.label,
					description: contactData.description,
					is_primary: contactData.isPrimary || false,
					is_verified: false,
				}
			}

			const response = await NotificationsApi.createContact(request)
			
			toast({
				title: t('contacts.success.created'),
				status: 'success',
				duration: 3000,
			})

			return response.data.contact || null
		} catch (error: any) {
			toast({
				title: t('contacts.error.createFailed'),
				description: error.response?.data?.message || error.message,
				status: 'error',
				duration: 5000,
			})
			return null
		} finally {
			setIsLoading(false)
		}
	}, [NotificationsApi, toast, t])

	// Update contact
	const updateContact = useCallback(async (
		contactId: string,
		contactData: Partial<ContactFormData>
	): Promise<boolean> => {
		setIsLoading(true)
		try {
			const request: CordeCommonModelsDtoNotificationsUpdateContactRequest = {
				contact: {
					id: contactId,
					type: contactData.type,
					value: contactData.value,
					label: contactData.label,
					description: contactData.description,
					is_primary: contactData.isPrimary,
				}
			}

			await NotificationsApi.updateContact(contactId, request)
			
			toast({
				title: t('contacts.success.updated'),
				status: 'success',
				duration: 3000,
			})

			return true
		} catch (error: any) {
			toast({
				title: t('contacts.error.updateFailed'),
				description: error.response?.data?.message || error.message,
				status: 'error',
				duration: 5000,
			})
			return false
		} finally {
			setIsLoading(false)
		}
	}, [NotificationsApi, toast, t])

	// Delete contact
	const deleteContact = useCallback(async (contactId: string): Promise<boolean> => {
		setIsLoading(true)
		try {
			await NotificationsApi.deleteContact(contactId)
			
			toast({
				title: t('contacts.success.deleted'),
				status: 'success',
				duration: 3000,
			})

			return true
		} catch (error: any) {
			toast({
				title: t('contacts.error.deleteFailed'),
				description: error.response?.data?.message || error.message,
				status: 'error',
				duration: 5000,
			})
			return false
		} finally {
			setIsLoading(false)
		}
	}, [NotificationsApi, toast, t])

	// List contacts for entity
	const listContacts = useCallback(async (
		entityType?: string,
		entityId?: string,
		contactType?: string,
		onlyPrimary?: boolean,
		onlyVerified?: boolean
	): Promise<ContactDto[]> => {
		try {
			const response = await NotificationsApi.listContacts(
				100,
				0,
				entityType,
				entityId,
				contactType,
				onlyPrimary
			)
			return response.data.contacts || []
		} catch (error: any) {
			console.error('Failed to load contacts:', error)
			return []
		}
	}, [NotificationsApi])

	// Search contacts
	const searchContacts = useCallback(async (
		searchQuery: string,
		contactType?: string,
		onlyVerified?: boolean
	): Promise<ContactDto[]> => {
		try {
			const response = await NotificationsApi.searchContacts(
				searchQuery,
				contactType,
				onlyVerified
			)
			return response.data.contacts || []
		} catch (error: any) {
			console.error('Failed to search contacts:', error)
			return []
		}
	}, [NotificationsApi])

	// Get contact by value
	const getContactByValue = useCallback(async (
		contactType: string,
		contactValue: string,
		entityType?: string,
		entityId?: string
	): Promise<ContactDto | null> => {
		try {
			const response = await NotificationsApi.getContactByValue(
				contactType,
				contactValue,
				entityType,
				entityId
			)
			return response.data.contact || null
		} catch (error: any) {
			console.error('Failed to get contact by value:', error)
			return null
		}
	}, [NotificationsApi])

	// Validate contact value
	const validateContactValue = useCallback(async (
		contactType: string,
		contactValue: string
	): Promise<{ isValid: boolean; message?: string; suggestions?: string[] }> => {
		try {
			const response = await NotificationsApi.validateContactValue(
				contactType,
				contactValue
			)
			return {
				isValid: response.data.is_valid || false,
				message: response.data.validation_message,
				suggestions: response.data.suggestions,
			}
		} catch (error: any) {
			console.error('Failed to validate contact value:', error)
			return { isValid: false, message: 'Validation failed' }
		}
	}, [NotificationsApi])

	// Verify contact
	const verifyContact = useCallback(async (
		contactId: string,
		verificationCode: string
	): Promise<boolean> => {
		setIsLoading(true)
		try {
			const verify: CordeCommonModelsDtoNotificationsVerifyContactRequest = {
				contact_id: contactId,
				verification_code: verificationCode,
			}

			await NotificationsApi.verifyContact(contactId, verify)
			
			toast({
				title: t('contacts.success.verified'),
				status: 'success',
				duration: 3000,
			})

			return true
		} catch (error: any) {
			toast({
				title: t('contacts.error.verificationFailed'),
				description: error.response?.data?.message || error.message,
				status: 'error',
				duration: 5000,
			})
			return false
		} finally {
			setIsLoading(false)
		}
	}, [NotificationsApi, toast, t])

	// Send contact verification
	const sendContactVerification = useCallback(async (contactId: string): Promise<boolean> => {
		setIsLoading(true)
		try {
			const verification = {
				contact_id: contactId,
			}
			await NotificationsApi.sendContactVerification(contactId, verification)
			
			toast({
				title: t('contacts.success.verificationSent'),
				status: 'success',
				duration: 3000,
			})

			return true
		} catch (error: any) {
			toast({
				title: t('contacts.error.verificationSendFailed'),
				description: error.response?.data?.message || error.message,
				status: 'error',
				duration: 5000,
			})
			return false
		} finally {
			setIsLoading(false)
		}
	}, [NotificationsApi, toast, t])

	// Set primary contact
	const setPrimaryContact = useCallback(async (
		entity: EntityDto,
		contactId: string,
		contactType: string
	): Promise<boolean> => {
		setIsLoading(true)
		try {
			const request: CordeCommonModelsDtoNotificationsSetPrimaryContactRequest = {
				entity,
				contact_id: contactId,
				contact_type: contactType,
			}

			await NotificationsApi.setPrimaryContact(request)
			
			toast({
				title: t('contacts.success.primarySet'),
				status: 'success',
				duration: 3000,
			})

			return true
		} catch (error: any) {
			toast({
				title: t('contacts.error.setPrimaryFailed'),
				description: error.response?.data?.message || error.message,
				status: 'error',
				duration: 5000,
			})
			return false
		} finally {
			setIsLoading(false)
		}
	}, [NotificationsApi, toast, t])

	return {
		isLoading,
		createContact,
		updateContact,
		deleteContact,
		listContacts,
		searchContacts,
		getContactByValue,
		validateContactValue,
		verifyContact,
		sendContactVerification,
		setPrimaryContact,
	}
} 